import { useMemo, useState } from "react";
import GeneralBaseModal from "../Modals/GeneralBaseModal";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import InlineSVG from "react-inlinesvg";
import { CSS } from "@dnd-kit/utilities";
import Auth from "../../Auth";
import useUserCustomization from "../../hooks/useUserCustomization";
import { CustomizeModalContent, RemoveButton, SortableItemContainer } from "./styles";
import classnames from "classnames";
import useInstalledPlugins from "../../hooks/useInstalledPlugins";
import { debounce } from "lodash";
import { InlineWarning } from "../InlineWarning";

const SortableItem = ({ installation, disabled, onUninstall }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: installation.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      className="list-item"
      style={style}
      ref={setNodeRef}
    >
      <SortableItemContainer>
        <div
          className={classnames("grabber", { disabled })}
          {...attributes}
          {...listeners}
        >
          <InlineSVG src="/assets/grabber.svg" />
        </div>
        <div className="item-content">
          <span>{installation.plugin.name}</span>
          <div className="actions">
            {!installation.builtIn && (
              <RemoveButton
                disabled={disabled}
                onClick={onUninstall}
              >
                Remove
              </RemoveButton>
            )}
            {installation.builtIn && <span className="built-in">Built-in</span>}
          </div>
        </div>
      </SortableItemContainer>
    </li>
  );
};

const reorderOperation = (draft, active, over) => {
  const oldIndex = draft.findIndex((field) => field.id === active.id);
  const newIndex = draft.findIndex((field) => field.id === over.id);
  const item = draft[oldIndex];

  draft.splice(oldIndex, 1);
  draft.splice(newIndex, 0, item);
  return [...draft];
};

export const CustomizePlugins = ({ isOpen, installations: initialInstallations, onDone, setOrderedTools, mainMenuCount }) => {
  const [installations, setInstallations] = useState(initialInstallations);
  const { refetch: refetchUserCustomization } = useUserCustomization();
  const { refetchInstallations } = useInstalledPlugins();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const reordered = reorderOperation(installations, active, over);
      setInstallations(reordered);
      setOrderedTools(reordered.map((inst) => inst.plugin.id));
      persistOrder(reordered);
    }
  };

  const persistOrder = useMemo(
    () =>
      debounce(async (reordered) => {
        const pluginOrder = reordered.map((inst) => inst.plugin.id);
        await Auth.fetchWithIdToken("/api/user-customization/save-plugin-order", {
          method: "PUT",
          pluginOrder,
        });
        refetchUserCustomization();
      }, 2000),
    [refetchUserCustomization]
  );

  const createUninstallHandler = (installation) => async () => {
    try {
      setError(null);
      setLoading(true);
      await Auth.fetchWithIdToken(`/api/installations/${installation.id}`, {
        method: "DELETE",
      });
      await refetchInstallations();
      setInstallations((draft) => {
        return draft.filter((installationToCheck) => installationToCheck.id !== installation.id);
      });
    } catch (error) {
      console.error(error);
      setError("Failed to uninstall plugin");
    } finally {
      setLoading(false);
    }
  };

  const handleIsOpenChange = (value) => {
    if (value === false) {
      onDone();
    }
  };

  return (
    <GeneralBaseModal
      isOpen={isOpen}
      setIsOpen={handleIsOpenChange}
      title={{
        main: `Re-order tools`,
        sub: "Drag and drop tools to customize where they live in Sudowrite.",
      }}
    >
      <CustomizeModalContent>
        <DndContext
          onDragEnd={handleDragEnd}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
        >
          <div className="modal-content">
            {error && (
              <div className="error">
                <InlineWarning text={error} />
              </div>
            )}
            <ul className="sortable-list">
              <li className="section-divider">Appear in the main menu</li>
              <SortableContext
                items={installations.map((installation) => installation.id)}
                strategy={verticalListSortingStrategy}
              >
                {installations.slice(0, mainMenuCount).map((installation) => (
                  <SortableItem
                    key={installation.plugin.id}
                    installation={installation}
                    disabled={loading}
                    onUninstall={createUninstallHandler(installation)}
                  />
                ))}

                {mainMenuCount < installations.length && (
                  <li
                    className="section-divider"
                    key="divider-more-tools"
                  >
                    <span>Appear under More Tools</span>
                  </li>
                )}

                {installations.slice(mainMenuCount).map((installation) => (
                  <SortableItem
                    key={installation.plugin.id}
                    installation={installation}
                    disabled={loading}
                    onUninstall={createUninstallHandler(installation)}
                  />
                ))}
              </SortableContext>
            </ul>
          </div>
        </DndContext>
      </CustomizeModalContent>
    </GeneralBaseModal>
  );
};
